import React, { useEffect, useState } from "react";
import MapComponent, { type LocationData } from "./MapComponent";
import type { RestaurantFormData } from "../restaurant.types";

interface LocationStepProps {
  formData: RestaurantFormData;
  updateFormData: (data: Partial<RestaurantFormData>) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isActive: boolean;
}

const LocationStep: React.FC<LocationStepProps> = ({
  formData,
  updateFormData,
  onSubmit,
  onCancel,
  isActive,
}) => {
  const [locationData, setLocationData] = useState<LocationData>({
    lat: formData.location.geo.coordinates[1],
    lng: formData.location.geo.coordinates[0],
    city: formData.location.city || "",
    address: formData.location.address || "",
    area: formData.location.area || "",
  });

  // Sync with parent form data
  useEffect(() => {
    updateFormData({
      location: {
        address: locationData.address,
        area: locationData.area,
        city: locationData.city,
        geo: {
          type: "Point",
          coordinates: [locationData.lng, locationData.lat],
        },
      },
    });
  }, [locationData, updateFormData]);

  return (
    <div className="space-y-2 p-1 overflow-hidden">
      <div className="space-y-4 p-1 overflow-hidden">

        {/* ---------- GROUP 1: Address + Area ---------- */}
        <div className="flex flex-col md:flex-row md:items-center md:gap-6 gap-4">

          {/* Address Field */}
          <div className="flex items-center gap-2 flex-1 min-w-[180px]">
            <label
              htmlFor="address"
              className="text-sm font-medium text-[#111827] whitespace-nowrap"
            >
              Address *
            </label>
            <input
              type="text"
              id="address"
              value={locationData.address}
              onChange={(e) =>
                setLocationData((prev) => ({
                  ...prev,
                  address: e.target.value,
                }))
              }
              className="flex-1 px-3 py-2 border border-[#FFDDA6] rounded-lg focus:ring-2 focus:ring-[#FFD166] focus:border-transparent transition-all text-sm"
              placeholder="Enter full address"
              required
            />
          </div>

          {/* Area Field */}
          <div className="flex items-center gap-2 flex-1 min-w-[180px]">
            <label
              htmlFor="area"
              className="text-sm font-medium text-[#111827] whitespace-nowrap"
            >
              Area / Neighborhood *
            </label>
            <input
              type="text"
              id="area"
              value={locationData.area}
              onChange={(e) =>
                setLocationData((prev) => ({
                  ...prev,
                  area: e.target.value,
                }))
              }
              className="flex-1 px-3 py-2 border border-[#FFDDA6] rounded-lg focus:ring-2 focus:ring-[#FFD166] focus:border-transparent transition-all text-sm"
              placeholder="Enter area/neighborhood"
              required
            />
          </div>

          {/* City Field */}
          <div className="flex items-center gap-2 flex-1 min-w-[180px]">
            <label
              htmlFor="city"
              className="text-sm font-medium text-[#111827] whitespace-nowrap"
            >
              City *
            </label>
            <input
              type="text"
              id="city"
              value={locationData.city}
              onChange={(e) =>
                setLocationData((prev) => ({
                  ...prev,
                  city: e.target.value,
                }))
              }
              className="flex-1 px-3 py-2 border border-[#FFDDA6] rounded-lg focus:ring-2 focus:ring-[#FFD166] focus:border-transparent transition-all text-sm"
              placeholder="Enter City Name"
              required
            />
          </div>

        </div>

        {/* ---------- GROUP 2: Latitude + Longitude ---------- */}
        <div className="flex flex-col md:flex-row md:items-center md:gap-6 gap-4">

          {/* Latitude Field */}
          <div className="flex items-center gap-2 flex-1 min-w-[150px]">
            <label
              htmlFor="lat"
              className="text-sm font-medium text-[#111827] whitespace-nowrap"
            >
              Latitude *
            </label>
            <input
              type="number"
              id="lat"
              value={locationData.lat}
              onChange={(e) =>
                setLocationData((prev) => ({
                  ...prev,
                  lat: parseFloat(e.target.value) || 0,
                }))
              }
              className="flex-1 px-3 py-2 border border-[#FFDDA6] rounded-lg focus:ring-2 focus:ring-[#FFD166] focus:border-transparent transition-all text-sm"
              placeholder="Latitude"
              required
            />
          </div>

          {/* Longitude Field */}
          <div className="flex items-center gap-2 flex-1 min-w-[150px]">
            <label
              htmlFor="lng"
              className="text-sm font-medium text-[#111827] whitespace-nowrap"
            >
              Longitude *
            </label>
            <input
              type="number"
              id="lng"
              value={locationData.lng}
              onChange={(e) =>
                setLocationData((prev) => ({
                  ...prev,
                  lng: parseFloat(e.target.value) || 0,
                }))
              }
              className="flex-1 px-3 py-2 border border-[#FFDDA6] rounded-lg focus:ring-2 focus:ring-[#FFD166] focus:border-transparent transition-all text-sm"
              placeholder="Longitude"
              required
            />
          </div>

        </div>

      </div>

      <span className="text-xs text-gray-500 mt-1">Click the map below to set the exact location of your restaurant.</span>
      {/* Map - Full width */}
      <div className={isActive ? "fade-in stagger-3 overflow-hidden" : "opacity-0 overflow-hidden"}>
        <MapComponent
          locationData={locationData}
          setLocationData={setLocationData}
          height="400px"
          searchable={true}
          interactive={true}
        />
      </div>
    </div>
  );
};

export default LocationStep;